import React, { useEffect, useState } from "react";
import { Card, Row, Col } from "react-bootstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getHost } from "../utils/utils";

const API_OVERVIEW = `${getHost()}/api/metrics/overview`;
const getToken = () => localStorage.getItem("jwt");

// Hardcoded fallback data
const FALLBACK_OPERATIONALS = 562;
const FALLBACK_PRODUCTS = 9314;

const generateLast30Days = () => {
  const dates = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    dates.push({
      date: `${date.getMonth() + 1}/${date.getDate()}`,
    });
  }
  return dates;
};

const FALLBACK_OPERATIONAL_DATA = (() => {
  const dates = generateLast30Days();
  return [
    { date: dates[0].date, value: 315 },
    { date: dates[3].date, value: 372 },
    { date: dates[6].date, value: 428 },
    { date: dates[9].date, value: 445 },
    { date: dates[12].date, value: 391 },
    { date: dates[15].date, value: 418 },
    { date: dates[18].date, value: 403 },
    { date: dates[21].date, value: 367 },
    { date: dates[24].date, value: 533 },
    { date: dates[27].date, value: 556 },
    { date: dates[29].date, value: 578 },
  ];
})();

const FALLBACK_PRODUCT_DATA = (() => {
  const dates = generateLast30Days();
  return [
    { date: dates[0].date, value: 5180 },
    { date: dates[3].date, value: 5520 },
    { date: dates[6].date, value: 5870 },
    { date: dates[9].date, value: 6150 },
    { date: dates[12].date, value: 6580 },
    { date: dates[15].date, value: 6920 },
    { date: dates[18].date, value: 7340 },
    { date: dates[21].date, value: 7760 },
    { date: dates[24].date, value: 8430 },
    { date: dates[27].date, value: 8950 },
    { date: dates[29].date, value: 9527 },
  ];
})();

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-dark border border-secondary rounded p-2">
        <p className="text-white mb-0 fs-12">
          {payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

const MonitorStats = () => {
  const [operationals] = useState(FALLBACK_OPERATIONALS);
  const [productsFound] = useState(FALLBACK_PRODUCTS);
  const [operationalsData, setOperationalsData] = useState(
    FALLBACK_OPERATIONAL_DATA,
  );
  const [productFoundData, setProductFoundData] = useState(
    FALLBACK_PRODUCT_DATA,
  );

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const res = await fetch(API_OVERVIEW, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) return;

        const data = await res.json();
        const metrics = data.metrics || [];

        if (metrics.length === 0) return;

        const operational = metrics.filter((m) => m.error_rate < 0.3).length;
        const totalProducts = metrics.reduce(
          (sum, m) => sum + (m.products_tracked || 0),
          0,
        );

        // Use the latest value as the last data point in the chart
        const dates = generateLast30Days();
        setOperationalsData((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            date: dates[29].date,
            value: operational,
          };
          return updated;
        });
        setProductFoundData((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            date: dates[29].date,
            value: totalProducts,
          };
          return updated;
        });
      } catch {
        // Fallback to hardcoded data on failure
      }
    };

    fetchOverview();
  }, []);

  return (
    <Row>
      {/* Operationals Card */}
      <Col xs={12} lg={6} className="mb-3 mb-lg-0">
        <Card className="border border-dark bg-transparent h-100">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <p className="text-white-65 fs-12 mb-1">Operationals</p>
                <h4 className="text-white fw-600 mb-0">
                  {operationals.toLocaleString()}
                </h4>
              </div>
              <div className="text-end">
                <p className="text-white-65 fs-11 mb-0">From:</p>
                <p className="text-white fs-11 mb-0">Last 30 Days</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={120}>
              <LineChart data={operationalsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis
                  dataKey="date"
                  stroke="#848484"
                  style={{ fontSize: "10px" }}
                  tick={{ fill: "#848484" }}
                />
                <YAxis
                  stroke="#848484"
                  style={{ fontSize: "10px" }}
                  tick={{ fill: "#848484" }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#00E98E"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>

      {/* Product Found Card */}
      <Col xs={12} lg={6}>
        <Card className="border border-dark bg-transparent h-100">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <p className="text-white-65 fs-12 mb-1">Product Found</p>
                <h4 className="text-white fw-600 mb-0">
                  {productsFound.toLocaleString()}
                </h4>
              </div>
              <div className="text-end">
                <p className="text-white-65 fs-11 mb-0">From:</p>
                <p className="text-white fs-11 mb-0">Last 30 Days</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={120}>
              <LineChart data={productFoundData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis
                  dataKey="date"
                  stroke="#848484"
                  style={{ fontSize: "10px" }}
                  tick={{ fill: "#848484" }}
                />
                <YAxis
                  stroke="#848484"
                  style={{ fontSize: "10px" }}
                  tick={{ fill: "#848484" }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#7C98FF"
                  strokeWidth={2}
                  dot={{ fill: "#7C98FF", r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default MonitorStats;
